<script setup lang="ts" generic="T extends Record<string, unknown>">
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'
import noItens from '@/assets/images/noItens.png'
import noResult from '@/assets/images/noResults.png'

const { t } = useI18n()
const { mobile } = useDisplay()
const isMobile = computed(() => mobile.value)

const emit = defineEmits<{
  (e: 'update-item', item: T): void
  (
    e: 'update',
    payload: { page: number; itemsPerPage: number; searchQuery: string }
  ): void
}>()

const props = withDefaults(
  defineProps<{
    items?: T[]
    itemsLength?: number
    currentPage?: number
    itemsPerPage?: number
    searchQuery?: string
    searchable?: boolean
    /** quando true, reserva espaço visual pro FAB e mantém o paginator central */
    contacts?: boolean
    importContacts?: boolean
  }>(),
  {
    items: () => [],
    itemsLength: 0,
    currentPage: 1,
    itemsPerPage: 10,
    searchQuery: '',
    searchable: false,
    contacts: false,
    importContacts: false,
  }
)

const page = ref(props.currentPage || 1)
const search = ref(props.searchQuery || '')

/* ---------- helpers de key sem any ---------- */
type MaybeWithId = Record<string, unknown> & { id?: string | number }
type MaybeWithUuid = Record<string, unknown> & { uuid?: string | number }

function hasId(obj: unknown): obj is MaybeWithId {
  if (obj == null || typeof obj !== 'object') return false
  const o = obj as { id?: unknown }
  return typeof o.id === 'string' || typeof o.id === 'number'
}
function hasUuid(obj: unknown): obj is MaybeWithUuid {
  if (obj == null || typeof obj !== 'object') return false
  const o = obj as { uuid?: unknown }
  return typeof o.uuid === 'string' || typeof o.uuid === 'number'
}
function getItemKey(item: T, index: number): string | number {
  if (hasId(item)) return item.id as string | number
  if (hasUuid(item)) return item.uuid as string | number
  return `row-${index}`
}

/* ---------- sincronizações ---------- */
watch(
  () => props.currentPage,
  (newPage) => (page.value = Number(newPage ?? 1))
)
watch(
  () => props.searchQuery,
  (val) => {
    if ((val ?? '') !== search.value) search.value = val ?? ''
  }
)

watch(search, (newSearch) => {
  emit('update', {
    page: 1,
    itemsPerPage: props.itemsPerPage,
    searchQuery: newSearch,
  })
  page.value = 1
})
watch(page, (newPage) => {
  emit('update', {
    page: newPage,
    itemsPerPage: props.itemsPerPage,
    searchQuery: search.value,
  })
})

/* ---------- computeds p/ UI e empty states ---------- */
const itemsCount = computed(() =>
  Number(
    (props.itemsLength ?? 0) ||
      (Array.isArray(props.items) ? props.items.length : 0)
  )
)
const hasItems = computed(() => itemsCount.value > 0)
const hasSearch = computed(() => (search.value ?? '').trim().length > 0)
const showSearchBar = computed(
  () => props.searchable && (hasItems.value || hasSearch.value)
)
const showEmptyA = computed(() => !hasSearch.value && !hasItems.value)
const showEmptyB = computed(() => hasSearch.value && !hasItems.value)
const showList = computed(() => hasItems.value)
const showPagination = computed(
  () => hasItems.value && itemsCount.value > Number(props.itemsPerPage || 10)
)
</script>

<template>
  <!-- Barra de busca -->
  <div
    v-if="showSearchBar"
    class="d-flex ga-6 rounded-lg bg-white"
    :class="contacts ? 'mr-16 pr-2' : 'px-4'"
    style="margin-bottom: 16px"
  >
    <v-text-field
      v-model="search"
      variant="underlined"
      density="compact"
      hide-details
      append-inner-icon="mdi-magnify"
      class="flex-grow-1 search-input"
      :placeholder="t('general.searchButton')"
    />
  </div>

  <div class="d-flex flex-column fill-height" style="width: 100%">
    <!-- Empty state A -->
    <template v-if="showEmptyA">
      <div class="empty-state">
        <div class="empty-content">
          <div class="empty-visual">
            <img :src="noItens" alt="Empty illustration" />
          </div>
          <h3 class="text-h6 font-weight-semibold text-grey-darken-3 mb-1">
            {{ t('general.nothingHere') }}
          </h3>
          <p
            class="text-body-2"
            style="line-height: 1.625; color: var(--v-theme-grey-darken-1)"
          >
            {{ t('general.createFirstItem') }}
          </p>
        </div>
      </div>
    </template>

    <!-- Empty state B -->
    <template v-else-if="showEmptyB">
      <div class="empty-state">
        <div class="empty-content">
          <div class="empty-visual">
            <img :src="noResult" alt="No results illustration" />
          </div>
          <h3 class="text-h6 font-weight-semibold text-grey-darken-3 mb-2">
            {{ t('general.tryAnotherSearch') }}
          </h3>
        </div>
      </div>
    </template>

    <!-- Lista -->
    <template v-else-if="showList">
      <div
        class="list-wrapper"
        :class="{
          'has-pagination': showPagination,
          'has-fab': contacts,
        }"
      >
        <div class="d-flex flex-column w-100 list-content">
          <slot name="mass-actions-content" />

          <div
            v-for="(item, idx) in items"
            :key="getItemKey(item, idx)"
            class="cursor-pointer"
            @click="emit('update-item', item)"
          >
            <slot name="item" v-bind="{ item }" />
          </div>
        </div>

        <!-- Paginação -->
        <div
          v-if="showPagination"
          class="ll-footer"
          :class="{ 'has-fab': contacts }"
        >
          <v-pagination
            v-model="page"
            class="eve-pagination"
            :length="Math.max(1, Math.ceil(itemsCount / Number(itemsPerPage)))"
            :total-visible="isMobile ? 5 : 7"
            :show-first-last-page="!isMobile"
            :size="isMobile ? 'large' : 'x-large'"
            density="comfortable"
            variant="text"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.search-input {
  border-radius: 8px;
}

/* Empty states */
.empty-state {
  height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}
.empty-content {
  width: 100%;
  max-width: 36rem;
  padding: 0 24px;
  text-align: center;
}
.empty-visual {
  width: 220px;
  height: 220px;
  margin: 0 auto;
  display: grid;
  place-items: center;
}
.empty-visual img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;
}

/* ----- LIST WRAPPER: padding só pra não deixar FAB cobrir o último card ----- */
/* Sem exagero: só reserva espaço quando tem FAB (contacts=true). */
.list-wrapper.has-fab {
  padding-bottom: 15px; /* espaço “real” pro FAB (bottom-14 + tamanho + respiro) */
}
.list-wrapper.has-pagination:not(.has-fab) {
  padding-bottom: 16px; /* só um respiro normal */
}

/* Conteúdo com gap */
.list-content {
  gap: 16px;
}

.ll-footer {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding-left: 16px;
  padding-top: 16px;
  padding-right: 16px;
  box-sizing: border-box;
}

/* reserva visual pro FAB sem “empurrar” a paginação pra esquerda */
.ll-footer.has-fab {
  grid-template-columns: 1fr auto 96px; /* reserva aproximada do FAB */
}

/* garante que o Vuetify fique no meio */
.ll-footer :deep(.v-pagination) {
  grid-column: 2;
  justify-self: center;
}

/* ===== ESTILO DA PAGINAÇÃO (compacto) ===== */
:deep(.eve-pagination .v-pagination__list) {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

/* Remove overlay padrão */
:deep(.eve-pagination .v-btn__overlay) {
  background-color: transparent !important;
}

:deep(.eve-pagination .v-pagination__item) {
  padding: 0 !important;
  margin: 0 !important;
  background: transparent !important;
  border-radius: 0 !important;
}

/* Hover para não ativos */
:deep(
  .eve-pagination
    .v-pagination__item:not(.v-pagination__item--is-active)
    .v-btn:hover
) {
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.18);
  border-color: rgba(0, 0, 0, 0.16);
}

/* Botões (números e setas) – menores pra não encostar no FAB */
:deep(.eve-pagination .v-pagination__item .v-btn),
:deep(.eve-pagination .v-pagination__prev .v-btn),
:deep(.eve-pagination .v-pagination__next .v-btn) {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  background-color: #ffffff;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(0, 0, 0, 0.08);

  --v-theme-overlay-multiplier: 0;
}

/* Ativo */
:deep(.eve-pagination .v-pagination__item--is-active .v-btn) {
  background-color: var(--standart-theme---EVE-primary, #8dc63f);
  border-color: var(--standart-theme---EVE-primary, #8dc63f);
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.12);
}

:deep(.eve-pagination .v-pagination__item--is-active .v-btn__content) {
  color: #fff;
  font-weight: 400;
}

/* Conteúdo do botão */
:deep(.eve-pagination .v-pagination__item .v-btn__content) {
  color: #333;
  font-family:
    'Roboto',
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  text-transform: none;
}

/* ===== RESPONSIVO ===== */
@media (max-width: 600px) {
  :deep(.eve-pagination .v-pagination__list) {
    gap: 8px;
  }

  :deep(.eve-pagination .v-pagination__item .v-btn),
  :deep(.eve-pagination .v-pagination__prev .v-btn),
  :deep(.eve-pagination .v-pagination__next .v-btn) {
    width: 34px;
    height: 34px;
  }

  /* fallback: ocultar first/last no mobile */
  :deep(.eve-pagination .v-pagination__first),
  :deep(.eve-pagination .v-pagination__last) {
    display: none !important;
  }
}
</style>
